/**
 * Human-readable text for the selected booking range, shown alongside the
 * calendar highlight (panel summary + mobile sticky bar). On mobile the start
 * day's highlight scrolls off when paging months, so this text is the only
 * persistent confirmation of what's selected. Pure + dependency-free; dates are
 * formatted in UTC to match the 'YYYY-MM-DD' booking-math contract (no local
 * timezone drift).
 */

function fmtShort(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}

/** 'Sun, Jun 28' — weekday-stamped, for the labelled Pickup/Return header cells. */
function fmtWeekday(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}

/**
 * 'Jun 28 – Jul 1' for a full range, 'Jun 28' while only the start is chosen,
 * null when nothing is selected (caller shows its own placeholder).
 */
export function formatRangeLabel(start: string | null, end: string | null): string | null {
  if (!start) return null
  if (!end) return fmtShort(start)
  return `${fmtShort(start)} – ${fmtShort(end)}`
}

export type RangeParts = { pickup: string; return: string | null }

/**
 * Structured pickup/return for the Airbnb-style two-field header. `return` is
 * null while the customer is mid-selection (only a start chosen), null overall
 * when nothing is selected. Weekday-stamped so a wrong day of week is catchable.
 */
export function rangeParts(start: string | null, end: string | null): RangeParts | null {
  if (!start) return null
  return { pickup: fmtWeekday(start), return: end ? fmtWeekday(end) : null }
}
